/**
 * Helpers de presentación del dominio Órdenes (formato de montos/fechas).
 *
 * Puro formato para la UI; sin lógica de negocio ni acceso a la API. Las fechas
 * llegan del backend ya en ISO/local Colombia (`progress/explore_orders_api.md`,
 * §9), así que aquí solo se acortan para mostrarlas; no se reinterpreta la zona.
 */

/** Formatea un monto en pesos colombianos (es-CO), sin decimales. */
export function formatAmount(value: number): string {
  return new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    maximumFractionDigits: 0,
  }).format(value);
}

/**
 * Acorta un ISO datetime a "YYYY-MM-DD HH:MM" para mostrar; una `date` pura
 * (sin "T") se deja tal cual. Devuelve "—" si el valor es nulo/vacío.
 */
export function formatDateTime(value: string | null): string {
  if (!value) {
    return "—";
  }
  const [date, time] = value.split("T");
  if (!time) {
    return date;
  }
  return `${date} ${time.slice(0, 5)}`;
}

/** Muestra una fecha (o "—" si es nula). */
export function formatDate(value: string | null): string {
  if (!value) {
    return "—";
  }
  return value.split("T")[0];
}

/**
 * Convierte una fecha ISO del backend (`YYYY-MM-DD`, posiblemente con hora) al
 * valor de un `<input type="date">` (`YYYY-MM-DD`). Devuelve "" si es nula.
 *
 * Sirve para **prellenar** el formulario de edición desde el detalle: el input
 * de fecha del navegador usa `YYYY-MM-DD`, que es justo el prefijo del ISO.
 */
export function isoToDateInput(value: string | null): string {
  if (!value) {
    return "";
  }
  return value.split("T")[0];
}

/**
 * Convierte la fecha de un `<input type="date">` (`YYYY-MM-DD`) al formato que el
 * backend espera en el cuerpo de alta/edición (`DD/MM/YYYY`, ver
 * `progress/explore_orders_api.md`, §9.1). Si no es `YYYY-MM-DD`, se devuelve tal
 * cual (el backend valida y reporta `invalid_*_date` si no lo entiende).
 */
export function dateInputToApi(value: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!match) {
    return value.trim();
  }
  const [, year, month, day] = match;
  return `${day}/${month}/${year}`;
}
